import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Sidebar } from "@/components/shell/Sidebar";
import { TopBar } from "@/components/shell/TopBar";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { NotificationsDrawer } from "@/components/shell/NotificationsDrawer";
import { QuickAddDialogs } from "@/components/shell/QuickAddDialogs";
import { BrandLoader } from "@/components/common/BrandIdentity";
import { PageTransition } from "@/motion";
import { useApp } from "@/context/AppContext";
import OrgPicker from "@/components/shell/OrgPicker";

export function AppShell() {
  const { auth, booting, ccore } = useApp();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [quickType, setQuickType] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ⌘K / Ctrl+K global palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // support ?new=1 deep link to open new matter
  useEffect(() => {
    if (searchParams.get("new") === "1" && location.pathname === "/matters") {
      setQuickType("matter");
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("new") === "1" && location.pathname === "/clients") {
      setQuickType("client");
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams, location.pathname]);

  if (booting) {
    return <BrandLoader />;
  }
  if (!auth) return <Navigate to="/login" replace state={{ from: location }} />;
  if (ccore?.needsPicker) return <OrgPicker />;
  const ccoreActive = (ccore?.memberships || []).filter((m) => m.status === "ACTIVE");
  // Guided onboarding only for genuinely new users: no C-Core org, no pending invite, and no
  // legacy organisation either. Existing workspaces and C-Core outages fall through to the app.
  if (ccore?.ready && !ccore.error && !ccore.orgId && ccoreActive.length === 0 && !(ccore.pendingInvites || []).length && !auth?.organisation) return <OrgPicker onboarding />;

  return (
    <div className="flex h-screen overflow-hidden bg-paper">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenNotifications={() => setNotifOpen(true)}
          onQuickAdd={setQuickType}
        />
        <main className="nyk-scroll relative flex-1 overflow-y-auto" data-testid="workspace-content">
          <span key={`brand-${location.pathname}`} className="brand-navigation-progress" aria-hidden="true" />
          <PageTransition routeKey={location.pathname} className="mx-auto max-w-[1440px] px-5 py-6 lg:px-8">
            {auth?.mfa?.ownerOptIn && !auth.mfa.enabled && <div className="mb-4 rounded-lg border border-copper/40 bg-copper-light p-3 text-sm" data-testid="owner-mfa-prompt">Your owner account has not enrolled an authenticator. Enrollment remains your choice. <a className="font-semibold underline" href="/settings?section=Security" data-testid="owner-mfa-enroll-link">Review security settings</a></div>}
            {ccore?.pendingInvites?.length > 0 && <div className="mb-4 rounded-lg border border-copper/40 bg-copper-light p-3 text-sm" data-testid="pending-invites-banner">You have {ccore.pendingInvites.length} pending organisation invitation{ccore.pendingInvites.length > 1 ? "s" : ""}. <a className="font-semibold underline" href="/settings?section=My Invitations" data-testid="pending-invites-link">Review invitations</a></div>}
            <Outlet />
          </PageTransition>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <NotificationsDrawer open={notifOpen} onOpenChange={setNotifOpen} />
      {quickType && <QuickAddDialogs type={quickType} onClose={() => setQuickType(null)} />}
    </div>
  );
}
